export const DASHBOARD_VIEWS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'heatmap-customer', label: 'Heatmap (Customer)' },
  { id: 'heatmap-ci', label: 'Heatmap (CI)' },
  { id: 'resilience', label: 'Resilience' },
  { id: 'incident', label: 'Incident' },
  { id: 'request', label: 'Service Request' },
  { id: 'case', label: 'Case' },
  { id: 'change-request', label: 'Change Request' },
  { id: 'problems', label: 'Problems' },
  { id: 'all-tickets', label: 'All Tickets' },
  { id: 'sla-view', label: 'SLA View' },
  { id: 'change-details', label: 'Change Details' },
  // { id: 'msr-report', label: 'MSR Report' }, // Hidden from KPI dashboard menu
] as const;

export type DashboardViewId = (typeof DASHBOARD_VIEWS)[number]['id'] | 'msr-report';

export interface Customer {
  id: string;
  accountName: string;
}

export interface Group {
  id: string;
  name: string;
}

/** Stored KPI duration value: `preset:*`, `custom:from,to`, or legacy `30`/`60`/`90`. */
export type KpiDurationFilterValue = string;

/** Resilience tab — MTTR heatmap, priority bars, weekly trend, assignment groups (from `/dashboard/charts`). */
export interface KpiResilienceData {
  mttrHeatmap: {
    weekLabels: string[];
    /** ISO week starts (YYYY-MM-DD); parallel to weekLabels for cross-filter clicks. */
    weekStarts: string[];
    weekdayLabels: string[];
    mttrByWeekAndWeekday: (number | null)[][];
    countsByWeekAndWeekday: number[][];
  };
  /** Critical, High, Moderate, Low, Planning — same order as KPI priority filter. */
  mttrByPriority: Array<{ priorityLabel: string; mttrMinutes: number | null; count: number }>;
  mttrTrendByWeek: Array<{
    weekLabel: string;
    weekStart: string;
    incidentMttrMinutes: number | null;
    requestMttrMinutes: number | null;
    incidentCount: number;
    requestCount: number;
  }>;
  mttrByAssignmentGroup: Array<{ groupName: string; mttrMinutes: number | null; count: number }>;
}

export interface KpiFilterState {
  duration: KpiDurationFilterValue;
  typeMulti: string[];
  statusMulti: string[];
  priorityFilter: string;
  customerId: string;
  assignmentGroupIds: string[];
  assignedToMe: string;
  assignedTo: string;
}

export interface KpiData {
  ticketsTotal: number;
  incidentsVolume: number;
  requestsVolume: number;
  casesVolume: number;
  /** Problems in KPI date range (same as summary). */
  problemsVolume: number;
  changesVolume: number;
  mttrMinutes: string;
  /** MTTR (minutes) for resolved incidents only. */
  incidentsMttrMinutes: string;
  /** MTTR (minutes) for resolved service requests only. */
  requestsMttrMinutes: string;
  changeFailureRate: string;
  /** % of changes in KPI range with success close code. */
  changesSuccessRate: string;
  /** MTTR (minutes) for resolved changes in sample. */
  changesMttrMinutes: string;
  ticketsAvgOpenDays: string;
  /** Average age in days for open incidents only. */
  incidentsAvgOpenDays: string;
  /** Average age in days for open service requests only. */
  requestsAvgOpenDays: string;
  /** Average age in days for open Case-type tickets only. */
  casesAvgOpenDays: string;
  caseResponseSlaMetRate: string;
  incidentOngoingSlaMetRate: string;
  requestOngoingSlaMetRate: string;
  incidentResolutionSlaMetRate: string;
  requestResolutionSlaMetRate: string;
  caseResponseSlaTicketCount: number;
  incidentOngoingSlaTicketCount: number;
  requestOngoingSlaTicketCount: number;
  incidentResolutionSlaTicketCount: number;
  requestResolutionSlaTicketCount: number;
  backlogAge: Record<string, number>;
  priorityCounts: Record<string, number>;
  slaBreachPct: Record<string, number>;
  slaBreachByType: Record<string, number>;
  /** Breached SLA instance counts (aligned with rate donuts); used for main dashboard SLA bar chart. */
  slaBreachBySlaMetric: {
    caseResponse: number;
    incidentOngoing: number;
    incidentResolution: number;
    requestOngoing: number;
    requestResolution: number;
  };
  monthlyTrend: number[];
  monthlyTrendCase: number[];
  monthlyTrendIncident: number[];
  monthlyTrendRequest: number[];
  /** Month axis labels (e.g. `Jan 25`) — same length as `monthlyTrendCase` */
  monthlyTrendMonthLabels: string[];
  monthlyTrendChange: number[];
  /** Problems created per month — same bucket span as other monthly trend arrays */
  monthlyTrendProblem: number[];
  /** % of problems in range with documented solution (proxy for knowledge). */
  problemsWithKnowledgePct: string;
  problemsVolumeByWeek: Array<{ weekLabel: string; weekStart: string; count: number }>;
  problemsStateCustomerHeatmap: {
    stateLabels: string[];
    stateKeys: string[];
    rows: Array<{ customerId: string; customerName: string; counts: number[] }>;
    maxCount: number;
  };
  problemsCustomerWeekHeatmap: {
    weekLabels: string[];
    weekStarts: string[];
    rows: Array<{ customerId: string; customerName: string; counts: number[] }>;
    maxCount: number;
  };
  problemsByResolutionCode: Array<{ key: string; label: string; count: number }>;
  problemsDetailPreview: Array<{
    id: string;
    number: string;
    stateLabel: string;
    openedAt: string;
    assignmentGroup: string | null;
    company: string | null;
    customerType?: string | null;
    knowledge: string;
    priorityLabel: string;
    problemStatement: string;
    resolutionLabel: string | null;
  }>;
  /** Open Incident & Service Request, age ≥2 days by bucket (current backlog; not scoped by KPI date range). */
  operationalAgeingByGroup: KpiOperationalAgeingRow[];
  /** Open Incident & Service Request by last update vs now (current backlog; not scoped by KPI date range). */
  lastUpdatedByGroup: KpiLastUpdatedRow[];
  /** Incidents in range by assignment group (top 8), with SLA breach % per group. */
  incidentsByAssignmentGroup: Array<{ groupName: string; count: number; slaBreachPct: number }>;
  /** Service requests in range by customer (treemap, top 40). */
  requestsByCustomerTreemap: Array<{ customerId: string; customerName: string; customerType?: string | null; count: number }>;
  /** Service requests in range by assignment group (top 8), with SLA breach % per group. */
  requestsByAssignmentGroup: Array<{ groupName: string; count: number; slaBreachPct: number }>;
  /** Case-type volume by week within KPI range. */
  casesVolumeByWeek: Array<{ weekLabel: string; weekStart: string; count: number }>;
  /** Cases in range by customer (treemap, top 40). */
  casesByCustomerTreemap: Array<{ customerId: string; customerName: string; customerType?: string | null; count: number }>;
  /** Cases in range by assignment group (top 8), with SLA breach % per group. */
  casesByAssignmentGroup: Array<{ groupName: string; count: number; slaBreachPct: number }>;
  changesByAssignmentGroup: Array<{ groupName: string; count: number; successPct: number }>;
  changesCloseCodeSlices: Array<{ key: string; label: string; count: number }>;
  changesByState: Array<{ stateLabel: string; stateKey: string; count: number }>;
  changesByChangeType: Array<{ typeLabel: string; typeKey: string; count: number }>;
  changeFailureRateByType: Array<{ typeLabel: string; typeKey: string; failurePct: number }>;
  resilience: KpiResilienceData;
}

export interface KpiOperationalAgeingRow {
  ticketType: string;
  assignmentGroupName: string;
  days2to5: number;
  days6to10: number;
  days11to15: number;
  days16to30: number;
  daysOver30: number;
  rowTotal: number;
}

export interface KpiLastUpdatedRow {
  ticketType: string;
  assignmentGroupName: string;
  within24h: number;
  over24h: number;
  rowTotal: number;
}

export const TYPE_OPTIONS = ['Incident', 'Request', 'Case', 'Change', 'Problem'].map((o) => ({ value: o, label: o }));
export const STATUS_OPTIONS = ['New', 'In Progress', 'Resolved', 'Closed', 'On Hold'].map((o) => ({ value: o, label: o }));

/** KPI filter + chart axis order (matches Case priority). */
export const KPI_PRIORITY_LABELS = ['Critical', 'High', 'Moderate', 'Low', 'Planning'] as const;
export type KpiPriorityLabel = (typeof KPI_PRIORITY_LABELS)[number];

export const PRIORITY_OPTIONS: string[] = ['All', ...KPI_PRIORITY_LABELS];

export const BACKLOG_LABELS = [
  'Within 24 hours',
  '> 24 h – < 2 days',
  '2 - 5 days',
  '6 - 10 Days',
  '11 - 15 Days',
  '16 - 30 Days',
  '> 30 Days',
];
export const BACKLOG_KEYS = ['within-24h', '24h-to-2d', '2-5', '6-10', '11-15', '16-30', '>30'];
export const BACKLOG_COLORS = [
  'rgba(125, 211, 252, 0.9)',
  'rgba(56, 189, 248, 0.85)',
  'rgba(59, 130, 246, 0.8)',
  'rgba(16, 185, 129, 0.8)',
  'rgba(245, 158, 11, 0.8)',
  'rgba(147, 197, 253, 0.9)',
  'rgba(34, 197, 94, 0.9)',
];
